/**
 * Data selection for the Strategic KPIs dashboard — pure functions over the engine's response,
 * unit-tested. This is PRESENTATION logic only: which KPIs count as "steered" (they have a
 * target), which value a node is judged by, how the traffic lights are counted and ranked, who
 * goes on the attention list and in which order. No KPI figure is computed here (trap 0 of the
 * module doc): every actual, target, YTD share, achievement and prior year is the engine's.
 *
 * Page-side derivations, explicitly: counting traffic lights, sorting and filtering by the
 * engine's `achievement`, and the direction of the actual against the prior year (a comparison,
 * never a number). A numeric delta vs prior year would be an engine field — not built.
 */
import type {
    ActualsResponse, GroupActuals, GroupKpiValue, GroupScopeType, KpiCatalogueEntry, KpiValue, PersonActuals,
} from '../types/strategicKpis';
import { CATEGORY_ORDER, trafficLight, type Light } from './strategicKpiFormat';

/**
 * The node the dashboard describes — the engine names it (`top`): the BU row, the TOTAL row of a
 * multi-BU request, or with a K3 selection the node it matches exactly, else SELECTION.
 */
export function topGroup(data: ActualsResponse): GroupActuals | null {
    const top = data.top ?? (data.bus.length > 1 ? { scopeType: 'TOTAL', scopeKey: data.bus.join(',') } : { scopeType: 'BU', scopeKey: data.bus[0] });
    return data.groups.find(g => g.scopeType === top.scopeType && g.scopeKey === top.scopeKey) ?? null;
}

/** "SPC", "All BUs", "Team Joaquin Xavier", "dit", "dit + dsp". */
export function scopeLabel(g: Pick<GroupActuals, 'scopeType' | 'label'>): string {
    return g.scopeType === 'TOTAL' ? 'All BUs' : g.scopeType === 'TEAM' ? `Team ${g.label}` : g.label;
}

/** The scope label of the top node. */
export function topLabel(data: ActualsResponse): string {
    const top = topGroup(data);
    return top ? scopeLabel(top) : data.bus.length > 1 ? 'All BUs' : data.bus[0];
}

/** BAB exists per whole company / BU only: a team, a person, SELECTION or a node the selection cuts has no BAB rows. */
export function hasBabRows(g: Pick<GroupActuals, 'scopeType' | 'partial'>): boolean {
    return (g.scopeType === 'COMPANY' || g.scopeType === 'BU' || g.scopeType === 'TOTAL') && !g.partial;
}

export const SCOPE_NOUN: Record<GroupScopeType, string> = {
    TOTAL: 'All BUs', BU: 'BU', COMPANY: 'Company', TEAM: 'Team', PERSON: 'Person', SELECTION: 'Selection',
};

/**
 * One value as the dashboard judges it. For a group node the stored group target comes first
 * and Σ of the person targets second (interview decision 1 — both exist independently, the
 * page shows both; the light follows the group target where one is stored). For a person the
 * stored person target. `target` / `targetYtd` are the figures the achievement is judged
 * against; `achievement` is the engine's (already inverted for lower-is-better KPIs).
 */
export interface JudgedValue {
    actual: number | null;
    prior: number | null;
    planned: number | undefined;
    target: number | null;
    targetYtd: number | null;
    achievement: number | null;
    hasTarget: boolean;
    hasGroupTarget: boolean;
    /** Group nodes only: Σ / pooled over the persons of the node (GP may differ from `actual`). */
    personsActual: number | null;
    personsTarget: number | null;
    noData: string | undefined;
}

const EMPTY: JudgedValue = {
    actual: null, prior: null, planned: undefined, target: null, targetYtd: null, achievement: null,
    hasTarget: false, hasGroupTarget: false, personsActual: null, personsTarget: null, noData: undefined,
};

export function judgeGroup(kpi: Pick<KpiCatalogueEntry, 'groupAggregation'>, v: GroupKpiValue | undefined): JudgedValue {
    if (!v) return EMPTY;
    const hasGroupTarget = v.target !== null;
    const hasPersonsTarget = v.personsTarget !== null;
    // Average % without a group target: the engine's group actual is a mean against nothing
    // (0 %); the mean vs the person targets is the figure — same rule as the light (KpiTiles P3).
    const actual = kpi.groupAggregation === 'mean' && !hasGroupTarget ? v.personsActual : v.actual;
    return {
        actual,
        prior: v.prior,
        planned: v.planned,
        target: hasGroupTarget ? v.target : v.personsTarget,
        targetYtd: hasGroupTarget ? v.targetYtd : v.personsTargetYtd,
        achievement: hasGroupTarget ? v.achievement : v.achievementVsPersons,
        hasTarget: hasGroupTarget || hasPersonsTarget,
        hasGroupTarget,
        personsActual: v.personsActual,
        personsTarget: v.personsTarget,
        noData: v.noData,
    };
}

export function judgePerson(v: KpiValue | undefined): JudgedValue {
    if (!v) return EMPTY;
    return {
        actual: v.actual, prior: v.prior, planned: v.planned, target: v.target, targetYtd: v.targetYtd,
        achievement: v.achievement, hasTarget: v.target !== null, hasGroupTarget: false,
        personsActual: null, personsTarget: null, noData: v.noData,
    };
}

/** The light of a judged value: no target or no achievement → 'none' (never red by default). */
export function lightOf(j: Pick<JudgedValue, 'achievement'>): Light {
    return trafficLight(j.achievement) ?? 'none';
}

/**
 * K5 (ND-03, Roman 2026-10-07): Market Data is a year-to-date count judged against the FULL-year
 * target (plan answer 13 — no day share), so it is "behind" by construction until December. Until
 * the as-of date reaches the year end it gets no light: a neutral "n % of the year target", left
 * out of the status card's counts and the attention list. Presentation only — the engine's
 * achievement is unchanged and the definition is Elena's.
 */
export function paceAtYearEnd(kpi: Pick<KpiCatalogueEntry, 'category' | 'windowType'>, yearFraction: number): boolean {
    return kpi.category === 'marketdata' && kpi.windowType === 'YTD' && yearFraction < 1;
}

/** The steered KPIs that carry a light today — everything except those judged at year end. */
export function judgedNow(steered: KpiCatalogueEntry[], yearFraction: number): KpiCatalogueEntry[] {
    return steered.filter(k => !paceAtYearEnd(k, yearFraction));
}

/** Derived KPIs that are shown as a line under their family rather than as a KPI of their own. */
const DERIVED_LINES = new Set(['visits.average_pct']);

/**
 * Steered = the top node has a target for it (group target or Σ person targets). A KPI moves up
 * as soon as a target is stored on the Targets tab — no configuration. Catalogue order kept.
 */
export function steeredKpis(data: ActualsResponse): KpiCatalogueEntry[] {
    const top = topGroup(data);
    if (!top) return [];
    return data.kpis.filter(k => !DERIVED_LINES.has(k.kpiKey) && judgeGroup(k, top.values[k.kpiKey]).hasTarget);
}

/**
 * K3b (E6, plan answer 11): the context section shows only these two — "just interesting to have
 * the data" (Elena). They carry no target since definition version 4. Every other KPI without a
 * target stays in the catalogue, the matrix and the Definitions tab, and appears among the steered
 * KPIs as soon as a target is set (K3c: the targets of 2027).
 */
export const CONTEXT_KPIS: readonly string[] = ['crm.success_rate_12m', 'crm.avg_days_to_close_12m'];

/** The context KPIs (CONTEXT_KPIS) that have no target at the top node — value and prior year only. */
export function contextKpis(data: ActualsResponse): KpiCatalogueEntry[] {
    const top = topGroup(data);
    const context = data.kpis.filter(k => CONTEXT_KPIS.includes(k.kpiKey));
    if (!top) return context;
    return context.filter(k => !judgeGroup(k, top.values[k.kpiKey]).hasTarget);
}

export type LightCounts = Record<Light, number>;

export function countLights(lights: Light[]): LightCounts {
    const c: LightCounts = { green: 0, yellow: 0, red: 0, none: 0 };
    for (const l of lights) c[l]++;
    return c;
}

// ---------------------------------------------------------------------------------------------
// Dashboard card head — what the former hero kept (E12: «n of m behind plan» and the year progress)
// ---------------------------------------------------------------------------------------------

export interface SteeredHead {
    /** Steered KPIs of the top node (all families, whatever the pills show). */
    steered: number;
    /** …of which carry a light today (ND-03: Market Data before the year end does not). */
    judged: number;
    /** …of which red (below 90 %). */
    behind: number;
    /** The engine's `yearFraction` as a whole percentage. */
    yearPct: number;
    /** Steered KPIs judged at the year end: counted in neither `judged` nor `behind`. */
    atYearEnd: KpiCatalogueEntry[];
}

/**
 * The numbers of the card head «Steered KPIs», from the same selectors the status hero used (`judgedNow`,
 * `judgeGroup`, `lightOf`, `countLights`): the count of red lights over the KPIs judged today, whatever
 * family the pills show. Nothing is computed here that the hero did not.
 */
export function steeredHead(data: ActualsResponse, steered: KpiCatalogueEntry[]): SteeredHead | null {
    const top = topGroup(data);
    if (!top) return null;
    const judged = judgedNow(steered, data.windows.yearFraction);
    const counts = countLights(judged.map(k => lightOf(judgeGroup(k, top.values[k.kpiKey]))));
    return {
        steered: steered.length,
        judged: judged.length,
        behind: counts.red,
        yearPct: Math.round(data.windows.yearFraction * 100),
        atYearEnd: steered.filter(k => !judged.includes(k)),
    };
}

/** «5 of 25 behind plan» as one sentence of the card head; the year share sits in the progress bar beside it. */
export function headSentence(h: Pick<SteeredHead, 'judged' | 'behind' | 'yearPct'>): string {
    if (h.judged === 0) return `No steered KPI is judged yet at ${h.yearPct} % of the year — the ones set are judged at the year end`;
    return h.behind === 0 ? 'Nothing behind plan' : `${h.behind} of ${h.judged} behind plan`;
}

/** The families that have a steered KPI, in the catalogue's family order — one pill each. */
export function steeredFamilies(steered: Pick<KpiCatalogueEntry, 'category'>[]): string[] {
    return CATEGORY_ORDER.filter(c => steered.some(k => k.category === c));
}

// ---------------------------------------------------------------------------------------------
// Leaderboard — one KPI, every person with a target for it
// ---------------------------------------------------------------------------------------------

export interface LeaderboardRow {
    person: PersonActuals;
    judged: JudgedValue;
    /** The person's team node (for the group-by header and the team pace), if the engine has one. */
    team: GroupActuals | null;
    company: GroupActuals | null;
}

export interface LeaderboardData {
    rows: LeaderboardRow[];
    /** Persons of the response without a stored target for this KPI — counted, never listed with zeros. */
    withoutTarget: number;
    /** The largest of actual and target over the rows — the common scale of the bars. */
    scaleMax: number;
}

export function leaderboardRows(data: ActualsResponse, kpiKey: string): LeaderboardData {
    const rows: LeaderboardRow[] = [];
    let withoutTarget = 0;
    let scaleMax = 0;
    for (const p of data.persons) {
        const j = judgePerson(p.values[kpiKey]);
        if (!j.hasTarget) { withoutTarget++; continue; }
        const team = p.teamManagerStaffId ? data.groups.find(g => g.scopeType === 'TEAM' && g.scopeKey === p.teamManagerStaffId) ?? null : null;
        const company = data.groups.find(g => g.scopeType === 'COMPANY' && g.scopeKey === p.companyCode) ?? null;
        rows.push({ person: p, judged: j, team, company });
        scaleMax = Math.max(scaleMax, (j.actual ?? 0) + (j.planned ?? 0), j.targetYtd ?? j.target ?? 0, j.prior ?? 0);
    }
    return { rows, withoutTarget, scaleMax };
}

/** Sort by the engine's achievement; rows without one go last in either direction. */
export function sortByAchievement<T extends { judged: Pick<JudgedValue, 'achievement'> }>(rows: T[], direction: 'asc' | 'desc'): T[] {
    return [...rows].sort((a, b) => {
        const av = a.judged.achievement, bv = b.judged.achievement;
        const aNil = av === null || !Number.isFinite(av), bNil = bv === null || !Number.isFinite(bv);
        if (aNil && bNil) return 0;
        if (aNil) return 1;
        if (bNil) return -1;
        return direction === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
}
